<script setup lang="ts">
import axios from 'axios'

import useUrl from '@/composables/url'
import AuthContainer from './components/AuthContainer.vue'
import AuthForm from './components/AuthForm.vue'
import AuthNewsPanel from './components/AuthNewsPanel.vue'
import PromptTypewriter from './components/PromptTypewriter.vue'
import type { AuthFormData } from '@/types/types'

const DEMO_URL = 'https://www.heypongo.com/je-veux-une-demo'

const GRADIENTS = [
  '/images/pongo-gradient-bg.webp',
  '/images/pongo-gradient-pink.webp',
  '/images/pongo-gradient-green.webp'
]

const { user, jwt, isSupportConnection, rememberMe } = useLocalStorage()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const notification = useNotification()
const { login, handleLoginRedirection } = useAuth()
const isLoaded = ref(true)

const handleSubmit = async (data: AuthFormData) => {
  if (!isLoaded.value) {
    return
  }
  isLoaded.value = false
  try {
    const authLogin = await axios.post('/auth/login', {
      email: data.email,
      password: data.password
    })

    if (authLogin.data.data.role.slug !== 'merchant') {
      throw 'Could not authenticate merchant user'
    }
    user.value = authLogin.data.data
    jwt.value = authLogin.data.jwt.accessToken

    await login()

    const hasToken = jwt.value?.length > 0

    if (hasToken) {
      rememberMe.value = data.rememberMe
      if (route.params.nextUrl != null) {
        router.push(route.params.nextUrl.toString())
      } else {
        isSupportConnection.value = false
        if (route.query.redirect_url) {
          router.push({ path: useUrl().formattedRedirectUrl(route.query.redirect_url) })
          return
        }
        handleLoginRedirection()
      }
    }
  } catch (error) {
    notification.create('error', {
      text: t(`login.error.${error.response?.status ?? 401}`)
    })
  } finally {
    isLoaded.value = true
  }
}
</script>

<template>
  <PartnerModal />
  <AuthContainer :should-show-redirect-to-login-link="false" :title="t('login.account.welcome')">
    <AuthForm :is-loaded="isLoaded" page-type="Login" @on-submit="handleSubmit" />

    <template #footer>
      <p class="text-sm text-muted-foreground">
        {{ t('login.account.noAccountYet') }}
        <a
          :href="DEMO_URL"
          target="_blank"
          rel="noopener"
          class="font-medium text-cta underline underline-offset-4 hover:text-cta/70"
        >
          {{ t('login.account.askForDemo') }}
        </a>
      </p>
    </template>

    <template #aside>
      <AuthNewsPanel
        :images="GRADIENTS"
        :label="t('login.news.label')"
        :title="t('login.news.title')"
        :subtitle="t('login.news.subtitle')"
      >
        <template #animation>
          <PromptTypewriter />
        </template>
      </AuthNewsPanel>
    </template>
  </AuthContainer>
</template>
